/**
 * pages/Dashboard.jsx
 * Premium dashboard shell with refined sidebar
 */

import { useState } from "react";
import { LogoLockup } from "../components/Logo";
import { Overview }       from "../modules/Overview";
import { IntelFeed }      from "../modules/AllModules";
import { JobScout }       from "../modules/AllModules";
import { ATSPipeline }    from "../modules/AllModules";
import { CRMView }        from "../modules/AllModules";
import { BriefMatching }  from "../modules/AllModules";
import { Sequences }      from "../modules/AllModules";
import { Interviews }     from "../modules/AllModules";
import { ClientPortal }   from "../modules/AllModules";
import { CVScreener }     from "../modules/CVScreener";

const NAV = [
  { group: 'Intelligence', icon:'◇', items: [
    { id:'overview',   label:'Overview',         icon:'◈', roles:['admin','sales','recruiter','client'] },
    { id:'intel',      label:'Intel feed',       icon:'⚡', roles:['admin','sales','recruiter'] },
  ]},
  { group: 'Recruitment', icon:'◇', items: [
    { id:'jobscout',   label:'Job Scout',        icon:'⊕', roles:['admin','sales','recruiter','subscriber'] },
    { id:'ats',        label:'ATS Pipeline',     icon:'◫', roles:['admin','recruiter'] },
    { id:'briefs',     label:'Brief matching',   icon:'◎', roles:['admin','sales','recruiter'] },
  ]},
  { group: 'Relationships', icon:'◇', items: [
    { id:'crm',        label:'CRM',              icon:'◷', roles:['admin','sales','recruiter'] },
    { id:'sequences',  label:'Sequences',        icon:'◈', roles:['admin','sales','recruiter'] },
  ]},
  { group: 'Screening', icon:'◇', items: [
    { id:'cvscreener', label:'CV Screener',      icon:'▣', roles:['admin','recruiter'] },
    { id:'interviews', label:'Interviews',       icon:'◉', roles:['admin','recruiter'] },
  ]},
  { group: 'Client', icon:'◇', items: [
    { id:'portal',     label:'Client Portal',    icon:'◌', roles:['admin','client'] },
  ]},
];

const TITLES = {
  overview:'Platform Overview', intel:'Intelligence Feed',
  jobscout:'Job Scout', ats:'ATS Pipeline', briefs:'Brief Matching',
  crm:'CRM', sequences:'Outreach Sequences', cvscreener:'CV Screener',
  interviews:'Voice Interviews', portal:'Client Portal',
};

const MODULES = {
  overview: <Overview/>, intel: <IntelFeed/>, jobscout: <JobScout/>,
  ats: <ATSPipeline/>, briefs: <BriefMatching/>, crm: <CRMView/>,
  sequences: <Sequences/>, cvscreener: <CVScreener/>,
  interviews: <Interviews/>, portal: <ClientPortal/>,
};

export function Dashboard({ user, onLogout }) {
  const [active, setActive] = useState(
    () => NAV.flatMap(g => g.items).find(i => i.roles.includes(user?.role))?.id ?? 'overview'
  );
  const [search, setSearch] = useState('');

  const canSee = item => item.roles.includes(user?.role);
  const initials = user?.name?.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2) || 'U';

  const filteredNav = NAV.map(g => ({
    ...g,
    items: g.items.filter(canSee).filter(i => !search || i.label.toLowerCase().includes(search.toLowerCase()))
  })).filter(g => g.items.length > 0);

  return (
    <div style={{display:'flex', height:'100vh', overflow:'hidden', background:'var(--bg)'}}>

      {/* SIDEBAR */}
      <div style={{width:240, flexShrink:0, background:'var(--sur)', borderRight:'1px solid var(--b)', display:'flex', flexDirection:'column'}}>

        {/* Logo */}
        <div style={{padding:'18px 18px 14px', borderBottom:'1px solid var(--b)'}}>
          <LogoLockup size={30} gap={10}/>
        </div>

        {/* Search */}
        <div style={{padding:'14px 14px 8px'}}>
          <div style={{position:'relative'}}>
            <span style={{position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', fontSize:12, color:'#666'}}>⌕</span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search modules..."
              style={{
                width:'100%', background:'var(--card2)', border:'1px solid var(--b)',
                borderRadius:8, padding:'7px 10px 7px 30px', fontSize:12, color:'#fff',
                fontFamily:'var(--mono)', outline:'none', transition:'border 0.15s',
              }}
              onFocus={e => e.target.style.borderColor = 'rgba(255,92,26,0.4)'}
              onBlur={e => e.target.style.borderColor = 'var(--b)'}
            />
          </div>
        </div>

        {/* Navigation */}
        <div style={{flex:1, overflowY:'auto', padding:'8px 0'}}>
          {filteredNav.length === 0 ? (
            <div style={{padding:'30px 18px', fontFamily:'var(--mono)', fontSize:11, color:'#666', textAlign:'center'}}>No matches</div>
          ) : filteredNav.map(group => (
            <div key={group.group} style={{marginBottom:6}}>
              <div style={{
                fontFamily:'var(--mono)', fontSize:9, textTransform:'uppercase',
                letterSpacing:'0.18em', color:'#555',
                padding:'12px 22px 6px', display:'flex', alignItems:'center', gap:6,
              }}>
                <span>{group.group}</span>
              </div>
              {group.items.map(item => {
                const isActive = active === item.id;
                return (
                  <div
                    key={item.id}
                    onClick={() => setActive(item.id)}
                    style={{
                      display:'flex', alignItems:'center', gap:10,
                      margin:'2px 10px', padding:'8px 12px',
                      borderRadius:8,
                      cursor:'pointer',
                      transition:'all 0.15s',
                      background: isActive
                        ? 'linear-gradient(90deg, rgba(255,112,9,0.18) 0%, rgba(255,6,99,0.10) 100%)'
                        : 'transparent',
                      border: `1px solid ${isActive ? 'rgba(255,92,26,0.25)' : 'transparent'}`,
                      fontSize: 13,
                      color: isActive ? '#fff' : '#999',
                      fontWeight: isActive ? 600 : 500,
                    }}
                    onMouseEnter={e => !isActive && (e.currentTarget.style.background = 'rgba(255,255,255,0.03)', e.currentTarget.style.color = '#ddd')}
                    onMouseLeave={e => !isActive && (e.currentTarget.style.background = 'transparent', e.currentTarget.style.color = '#999')}
                  >
                    <span style={{fontSize:13, width:18, textAlign:'center', color: isActive ? 'var(--or)' : 'inherit'}}>{item.icon}</span>
                    {item.label}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* User profile */}
        <div style={{padding:'12px', borderTop:'1px solid var(--b)', background:'var(--card)'}}>
          <div style={{display:'flex', alignItems:'center', gap:10, padding:'8px', borderRadius:10, background:'var(--card2)', border:'1px solid var(--b)'}}>
            <div style={{
              width:34, height:34, borderRadius:9,
              background:'linear-gradient(135deg, var(--or-start), var(--or-end))',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:12, fontWeight:700, color:'#fff', flexShrink:0,
            }}>{initials}</div>
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontSize:12, fontWeight:600, color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{user?.name}</div>
              <div style={{fontSize:9, color:'var(--gd)', fontFamily:'var(--mono)', textTransform:'uppercase', letterSpacing:'0.1em', fontWeight:600}}>{user?.role}</div>
            </div>
            <button
              onClick={onLogout}
              title="Sign out"
              style={{
                background:'transparent', border:'1px solid var(--b)', color:'#888',
                width:28, height:28, borderRadius:7, cursor:'pointer',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:12, transition:'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(248,113,113,0.3)'; e.currentTarget.style.color='#f87171'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor='var(--b)'; e.currentTarget.style.color='#888'; }}
            >⏻</button>
          </div>
        </div>
      </div>

      {/* MAIN */}
      <div style={{flex:1, display:'flex', flexDirection:'column', overflow:'hidden'}}>
        <div style={{
          height:54, background:'var(--sur)', borderBottom:'1px solid var(--b)',
          display:'flex', alignItems:'center', padding:'0 28px', gap:14, flexShrink:0,
        }}>
          <div style={{fontSize:15, fontWeight:600, flex:1, color:'#fff', letterSpacing:'-0.01em'}}>{TITLES[active]}</div>
          <span className="badge or" style={{fontSize:10, padding:'3px 10px'}}>AI-POWERED</span>
          <button className="btn sm" style={{padding:'5px 12px'}}>⚙ Settings</button>
        </div>
        <div style={{flex:1, overflowY:'auto', padding:'24px 28px', display:'flex', flexDirection:'column', gap:20}}>
          {MODULES[active]}
        </div>
      </div>
    </div>
  );
}
